import { useEffect, useMemo, useState } from 'react';
import { parseArticleDate } from '../../../utils/articleUtils';
import { ALL_YEARS } from '../constants';

export const useArticlesFilters = (articles) => {
  const articlesWithDates = useMemo(() => {
    return articles.map((article) => {
      const parsed = parseArticleDate(article.publishedAt);
      return {
        ...article,
        parsedDate: parsed ? parsed.date : null,
        isDateOnly: parsed ? parsed.isDateOnly : false,
      };
    });
  }, [articles]);

  const years = useMemo(() => {
    const yearSet = new Set();
    articlesWithDates.forEach((article) => {
      if (article.parsedDate) {
        const year = article.isDateOnly
          ? article.parsedDate.getUTCFullYear()
          : article.parsedDate.getFullYear();
        yearSet.add(year);
      }
    });
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [articlesWithDates]);

  const [selectedYear, setSelectedYear] = useState(() => ALL_YEARS);
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    if (!selectedYear && years.length > 0) {
      setSelectedYear(years[0]);
    }
  }, [selectedYear, years]);

  useEffect(() => {
    setSelectedMonth(null);
  }, [selectedYear]);

  const months = useMemo(() => {
    if (!selectedYear || selectedYear === ALL_YEARS) {
      return [];
    }
    const monthSet = new Map();
    articlesWithDates.forEach((article) => {
      if (!article.parsedDate) {
        return;
      }
      const year = article.isDateOnly
        ? article.parsedDate.getUTCFullYear()
        : article.parsedDate.getFullYear();
      if (year !== selectedYear) {
        return;
      }
      const monthIndex = article.isDateOnly
        ? article.parsedDate.getUTCMonth()
        : article.parsedDate.getMonth();
      if (!monthSet.has(monthIndex)) {
        const label = article.parsedDate.toLocaleDateString('pt-BR', {
          month: 'long',
          ...(article.isDateOnly ? { timeZone: 'UTC' } : {}),
        });
        const formatted = label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
        monthSet.set(monthIndex, { value: monthIndex, label: formatted });
      }
    });
    return Array.from(monthSet.values()).sort((a, b) => b.value - a.value);
  }, [articlesWithDates, selectedYear]);

  const filteredArticles = useMemo(() => {
    const isAllYears = selectedYear === ALL_YEARS;
    const list = articlesWithDates.filter((article) => {
      if (!article.parsedDate) {
        return isAllYears || !selectedYear;
      }
      if (selectedYear && !isAllYears) {
        const year = article.isDateOnly
          ? article.parsedDate.getUTCFullYear()
          : article.parsedDate.getFullYear();
        if (year !== selectedYear) {
          return false;
        }
      }
      if (selectedMonth !== null && !isAllYears) {
        const month = article.isDateOnly
          ? article.parsedDate.getUTCMonth()
          : article.parsedDate.getMonth();
        if (month !== selectedMonth) {
          return false;
        }
      }
      return true;
    });

    return list.sort((a, b) => {
      const aTime = a.parsedDate ? a.parsedDate.getTime() : 0;
      const bTime = b.parsedDate ? b.parsedDate.getTime() : 0;
      return bTime - aTime;
    });
  }, [articlesWithDates, selectedMonth, selectedYear]);

  const handleYearChange = (event) => {
    const { value } = event.target;
    if (value === ALL_YEARS) {
      setSelectedYear(ALL_YEARS);
      return;
    }
    const parsed = Number(value);
    setSelectedYear(Number.isNaN(parsed) ? null : parsed);
  };

  return {
    filteredArticles,
    months,
    selectedMonth,
    selectedYear,
    setSelectedMonth,
    years,
    handleYearChange,
  };
};
